import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { getUserPermissions, setUserPermissions, type AdminUser, type UserPermissionModule } from '@/api/admin';

type Override = boolean | null; // null = inherit role
type State = Record<string, Override>;

/**
 * Per-user permission overrides. Each module defaults to the user's role; set a
 * module to Allow or Deny to override it for this user only (Inherit keeps the
 * role's value). Only overridden modules are persisted.
 */
export function UserPermissionsModal({
  open,
  user,
  onClose,
}: {
  open: boolean;
  user: AdminUser | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [state, setState] = useState<State>({});

  const permQ = useQuery({
    queryKey: ['user-permissions', user?.id],
    queryFn: () => getUserPermissions(user!.id),
    enabled: open && !!user,
  });

  useEffect(() => {
    if (!permQ.data) return;
    const s: State = {};
    permQ.data.modules.forEach((m) => { s[m.id] = m.override; });
    setState(s);
  }, [permQ.data]);

  const modules = permQ.data?.modules ?? [];
  const isSuper = permQ.data?.isSuperAdmin ?? false;

  const groups = useMemo(() => {
    const map = new Map<string, UserPermissionModule[]>();
    modules.forEach((m) => {
      const g = map.get(m.group) ?? [];
      g.push(m);
      map.set(m.group, g);
    });
    return [...map.entries()];
  }, [modules]);

  const overrideCount = Object.values(state).filter((v) => v !== null).length;

  const saveMut = useMutation({
    mutationFn: () => {
      const overrides = Object.entries(state)
        .filter(([, v]) => v !== null)
        .map(([moduleId, allowed]) => ({ moduleId, allowed: allowed as boolean }));
      return setUserPermissions(user!.id, overrides);
    },
    onSuccess: (r) => {
      toast.push(`Saved — ${r.count} override${r.count === 1 ? '' : 's'} for ${user!.fullName}`, 'success');
      qc.invalidateQueries({ queryKey: ['user-permissions', user?.id] });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Save failed'), 'error'),
  });

  function resetAll() {
    setState((s) => {
      const n = { ...s };
      Object.keys(n).forEach((k) => (n[k] = null));
      return n;
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={user ? `Permissions — ${user.fullName}` : 'Permissions'}
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => saveMut.mutate()} disabled={saveMut.isPending || isSuper}>
            {saveMut.isPending ? 'Saving…' : 'Save Overrides'}
          </Button>
        </>
      }
    >
      {permQ.isLoading ? (
        <div className="py-10 text-center text-sm text-[var(--text-muted)]">Loading…</div>
      ) : isSuper ? (
        <div className="rounded-lg border border-info/30 bg-info/[0.06] px-3 py-3 text-sm text-[var(--text-secondary)]">
          <strong>SUPER_ADMIN</strong> already has full access to every module — per-user overrides don't apply.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* Summary / help */}
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-soft)] px-3 py-2">
            <div className="text-[11px] text-[var(--text-muted)]">
              Defaults come from role <span className="font-bold text-[var(--text-primary)]">{permQ.data?.roleName}</span>.
              Set a module to <span className="font-semibold text-success">Allow</span> or{' '}
              <span className="font-semibold text-danger">Deny</span> to override it for this user only.
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-bold text-accent">
                {overrideCount} override{overrideCount === 1 ? '' : 's'}
              </span>
              <button
                type="button"
                onClick={resetAll}
                disabled={overrideCount === 0}
                className="rounded-md border border-[var(--border-color)] px-2 py-1 text-[11px] font-bold text-[var(--text-secondary)] transition hover:border-accent hover:text-accent disabled:opacity-40"
              >
                Reset all to role
              </button>
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-3 px-1 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
            <span>Inherit = use role</span>
            <span className="text-success">Allow = force on</span>
            <span className="text-danger">Deny = force off</span>
          </div>

          {/* Grouped module list */}
          <div className="max-h-[52vh] overflow-y-auto rounded-lg border border-[var(--border-color)]">
            {groups.map(([group, mods]) => (
              <div key={group}>
                <div className="sticky top-0 z-10 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-widest text-[var(--text-muted)]">
                  {group}
                </div>
                {mods.map((m) => {
                  const val = state[m.id] ?? null;
                  const overridden = val !== null;
                  return (
                    <div
                      key={m.id}
                      className={
                        'flex items-center justify-between gap-3 border-b border-[var(--border-color)] px-3 py-1.5 last:border-b-0 ' +
                        (overridden ? 'bg-accent/[0.04]' : '')
                      }
                    >
                      <div className="min-w-0">
                        <div className="truncate text-[13px] font-semibold text-[var(--text-primary)]">{m.label}</div>
                        <div className="text-[10px] text-[var(--text-muted)]">
                          Role default: <span className={m.roleAllowed ? 'text-success' : 'text-danger'}>{m.roleAllowed ? 'Allowed' : 'Denied'}</span>
                          {overridden && <span className="ml-1 font-bold text-accent">· overridden</span>}
                        </div>
                      </div>
                      <TriToggle value={val} onChange={(v) => setState((s) => ({ ...s, [m.id]: v }))} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

function TriToggle({ value, onChange }: { value: Override; onChange: (v: Override) => void }) {
  const opts: { v: Override; label: string; active: string }[] = [
    { v: null, label: 'Inherit', active: 'bg-[var(--text-muted)] text-white' },
    { v: true, label: 'Allow', active: 'bg-success text-white' },
    { v: false, label: 'Deny', active: 'bg-danger text-white' },
  ];
  return (
    <div className="inline-flex shrink-0 overflow-hidden rounded-lg border border-[var(--border-color)]">
      {opts.map((o) => {
        const on = value === o.v;
        return (
          <button
            key={String(o.v)}
            type="button"
            onClick={() => onChange(o.v)}
            className={
              'px-2.5 py-1 text-[11px] font-bold transition ' +
              (on ? o.active : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)]')
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
